import { canReadArea, canReadAudit, canReadBelegung, canWriteArea, hasRight, type PublicUserDTO } from '@wf/shared'

export interface NavItem {
  to: string
  label: string
  /** Nur UX-Sichtbarkeit — der Server erzwingt Rechte ohnehin (Client zeigt nur, was sinnvoll ist). */
  visible: (user: PublicUserDTO) => boolean
}

/** Ein aufklappbares Tab-Menü, das mehrere {@link NavItem}s unter einer Beschriftung bündelt. */
export interface NavGroup {
  label: string
  items: NavItem[]
}

export type NavEntry = NavItem | NavGroup

/** Typguard: ist der Eintrag ein aufklappbares Menü (statt eines einzelnen Tabs)? */
export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry
}

// Screens werden inkrementell freigeschaltet — der Tab erscheint erst, wenn die Route existiert.
//
// 'Übersicht' ist der erste Reiter und die Default-Landung nach dem Login; die globale Auftragssuche ist
// dort eingebettet (ersetzt den früheren eigenen 'Suche'-Reiter; die /search-Route entfällt).
// 'Aufträge' (/orders) ist bewusst KEIN Tab mehr: das All-Auftrags-Board deckt sich mit Werkstatt +
// Meine Liste und wird über die Übersicht/Direktlink erreicht. Die Route /orders lebt weiter.
//
// Selten gebrauchte/„Büro"-Screens stecken in zwei Klappmenüs, damit die Leiste nicht überläuft:
//   Abrechnung    → Rechnungen, Offene Posten, Archiv
//   Einstellungen → Leistungen, Team, Werkstatt-Profil
//
// Namens-Klarheit (13.07., Folge des Team-Rechte-Editors): „Werkstatt-Board" (Arbeitssteuerung/Plantafel)
// vs. „Werkstatt-Profil" (Betriebs-Stammdaten) — identisch mit den Bereichs-Labels der Rechte-Matrix.
export const NAV: NavEntry[] = [
  { to: '/overview', label: 'Übersicht', visible: () => true },
  // Annahme als Klappmenü (Simons Entscheidung 15.07. abends: die Leiste soll nicht ausarten) —
  // bündelt das Annahme-Board und die Belegungs-Plantafel (eigenständiges Rechte-Paar 'belegung',
  // kein AREAS-Bereich → canReadBelegung). Ein reiner Planer sieht das Menü nur mit der Plantafel.
  {
    label: 'Annahme',
    items: [
      { to: '/intake', label: 'Annahme-Board', visible: (u) => canReadArea(u, 'annahme') },
      { to: '/planung', label: 'Plantafel', visible: (u) => canReadBelegung(u) },
    ],
  },
  { to: '/workshop', label: 'Werkstatt-Board', visible: (u) => canReadArea(u, 'werkstatt') },
  { to: '/my', label: 'Meine Liste', visible: (u) => hasRight(u, 'meine_auftraege') },
  // Zeiterfassung ist für JEDE Rolle da (eigenes Stempeln + Art.-15-Einsicht); Chef-Bereiche gated die Seite selbst.
  { to: '/zeiten', label: 'Zeiten', visible: () => true },
  { to: '/inventory', label: 'Inventar', visible: (u) => canReadArea(u, 'inventar') },
  { to: '/einkauf', label: 'Einkauf', visible: (u) => canReadArea(u, 'einkauf') },
  { to: '/customers', label: 'Kunden', visible: (u) => canReadArea(u, 'kunden') },
  {
    label: 'Abrechnung',
    items: [
      { to: '/invoices', label: 'Rechnungen', visible: (u) => canReadArea(u, 'rechnungen') },
      { to: '/open', label: 'Offene Posten', visible: (u) => canReadArea(u, 'rechnungen') },
      { to: '/archive', label: 'Archiv', visible: (u) => hasRight(u, 'alle_auftraege') },
    ],
  },
  {
    label: 'Einstellungen',
    items: [
      { to: '/prices', label: 'Leistungen', visible: (u) => canReadArea(u, 'leistungen') },
      { to: '/team', label: 'Team', visible: (u) => canWriteArea(u, 'team') },
      // Audit-Log-Viewer (16.07.): Nachweiskette der Werkstatt — eigenes Nicht-AREAS-Recht (Floor 90).
      { to: '/protokoll', label: 'Protokoll', visible: (u) => canReadAudit(u) },
      { to: '/settings', label: 'Werkstatt-Profil', visible: (u) => canWriteArea(u, 'werkstatt_einstellungen') },
      // Abo & Lizenzen (nur Chef): dieselbe Bedingung, die GET/POST /billing/* serverseitig guarden.
      { to: '/einstellungen/abo', label: 'Abo & Lizenzen', visible: (u) => canWriteArea(u, 'werkstatt_einstellungen') },
    ],
  },
]

/** Reiter des Betreiber-Kontos. Der Super-Admin hat keinen Mandanten → er sieht NUR die App-Verwaltung. */
const ADMIN_ENTRY: NavItem = { to: '/admin', label: 'App-Verwaltung', visible: (u) => u.isSuperadmin }

/**
 * Liefert die für den Nutzer sichtbaren Navi-Einträge. Menüs werden auf ihre sichtbaren Items
 * gefiltert und ganz weggelassen, wenn nichts übrig bleibt (kein leeres „▾"-Menü).
 * `impersonating`: im Support-Modus arbeitet der Betreiber ALS Werkstatt → normale Werkstatt-Tabs
 * (hasRight bypasst für den Superadmin, also alle sichtbar) statt der App-Verwaltung.
 */
export function visibleNav(user: PublicUserDTO, impersonating = false): NavEntry[] {
  // Super-Admin: mandantenübergreifend, kein eigener Werkstatt-Kontext → ausschließlich die App-Verwaltung
  // (alle Werkstatt-Tabs wären für ihn leer bzw. würden serverseitig 403en).
  if (user.isSuperadmin && !impersonating) return [ADMIN_ENTRY]
  const result: NavEntry[] = []
  for (const entry of NAV) {
    if (isNavGroup(entry)) {
      const items = entry.items.filter((item) => item.visible(user))
      if (items.length > 0) result.push({ label: entry.label, items })
    } else if (entry.visible(user)) {
      result.push(entry)
    }
  }
  return result
}
